import type { ButtonHTMLAttributes } from "react";
import { cn } from "../lib/cn.ts";

const variantClasses = {
  primary: [
    "bg-[var(--sf-primary)] text-white border-[var(--sf-primary)]",
    "hover:bg-[var(--sf-primary-hover)] hover:border-[var(--sf-primary-hover)]",
    "focus-visible:ring-[var(--sf-primary)]",
    "shadow-[0_1px_2px_rgba(79,70,229,0.25)]",
  ],
  secondary: [
    "bg-[var(--sf-surface)] text-[var(--sf-text)] border-[var(--sf-border-strong)]",
    "hover:bg-[var(--sf-surface-2)]",
    "focus-visible:ring-[var(--sf-border-strong)]",
  ],
  ghost: [
    "bg-transparent text-[var(--sf-text)] border-transparent",
    "hover:bg-[var(--sf-surface-2)]",
    "focus-visible:ring-[var(--sf-border)]",
  ],
  danger: [
    "bg-[var(--sf-danger)] text-white border-[var(--sf-danger)]",
    "hover:opacity-90",
    "focus-visible:ring-[var(--sf-danger)]",
  ],
} as const;

const sizeClasses = {
  sm: "h-9 px-3.5 lg:h-7 lg:px-3 rounded-lg lg:rounded-[5px] text-[13px] lg:text-xs font-medium gap-1.5",
  md: "h-10 px-3.5 lg:h-[34px] lg:px-3.5 rounded-lg lg:rounded-[var(--sf-radius)] text-[14px] lg:text-[12.5px] font-semibold gap-1.5",
  lg: "h-11 px-4 lg:h-10 lg:px-4 rounded-xl lg:rounded-[var(--sf-radius)] text-[15px] lg:text-sm font-semibold gap-2",
  icon: "h-10 w-10 lg:h-8 lg:w-8 p-0 rounded-lg lg:rounded-[var(--sf-radius)]",
} as const;

export type ButtonVariant = keyof typeof variantClasses;
export type ButtonSize = keyof typeof sizeClasses;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

export function Button({
  variant = "primary",
  size = "md",
  className,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "inline-flex items-center justify-center border font-[inherit] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        ...variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  );
}
